import { useState, useMemo } from "react";
import { api, apiErr } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Modal } from "@/lib/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ScanLine, Trash2, Plus } from "lucide-react";

function keyOf(f, l, g) { if (!f || !l || !g) return ""; return `${f.trim().toLowerCase()}|${l.trim().toLowerCase()}|${g}`; }

export function RosterImport({ open, onClose, grades = [], existing = [], onDone }) {
  const { L } = useLang();
  const [scanning, setScanning] = useState(false);
  const [creating, setCreating] = useState(false);
  const [rows, setRows] = useState([]);
  const [seq, setSeq] = useState(0);
  const existingKeys = useMemo(() => { const s = new Set(); (existing || []).forEach((st) => { const k = keyOf(st.first_name, st.last_name, st.grade_level); if (k) s.add(k); }); return s; }, [existing]);
  const isDup = (r) => existingKeys.has(keyOf(r.first_name, r.last_name, r.grade_level));
  const isReady = (r) => r.first_name.trim() && r.last_name.trim() && r.grade_level && (!isDup(r) || r.include);
  const dupCount = rows.filter((r) => isDup(r) && !r.include).length;

  const close = () => { setRows([]); onClose(); };

  const scan = async (file) => {
    if (!file) return;
    setScanning(true); setRows([]);
    const fd = new FormData(); fd.append("file", file);
    try {
      const r = await api.raw.post("/extract/roster/student", fd).then((x) => x.data);
      let n = seq;
      const list = (r.rows || []).map((g) => ({ _id: n++, first_name: g.first_name || "", middle_name: g.middle_name || "", last_name: g.last_name || "", dob: g.dob || "", gender: g.gender || "", grade_level: g.grade_level || "", student_email: g.student_email || "", parent_name: g.parent_name || "", parent_email: g.parent_email || "" }));
      setSeq(n); setRows(list);
      toast.success(L(`${list.length} students detected — review then create`, `${list.length} élèves détectés — vérifiez puis créez`));
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setScanning(false); }
  };

  const upd = (id, k, v) => setRows((rs) => rs.map((r) => (r._id === id ? { ...r, [k]: v } : r)));
  const removeRow = (id) => setRows((rs) => rs.filter((r) => r._id !== id));
  const addRow = () => { const id = seq; setSeq(seq + 1); setRows((rs) => [...rs, { _id: id, first_name: "", middle_name: "", last_name: "", dob: "", gender: "", grade_level: "", student_email: "", parent_name: "", parent_email: "" }]); };

  const create = async () => {
    const valid = rows.filter(isReady);
    if (valid.length === 0) { toast.error(L("Add at least one complete row (name + grade)", "Ajoutez au moins une ligne complète (nom + niveau)")); return; }
    setCreating(true);
    try {
      const payload = { students: valid.map((r) => ({ first_name: r.first_name.trim(), middle_name: r.middle_name?.trim() || null, last_name: r.last_name.trim(), dob: r.dob || null, gender: r.gender || null, grade_level: r.grade_level, student_email: r.student_email || null, parent_name: r.parent_name?.trim() || null, parent_email: r.parent_email?.trim() || null })) };
      const res = await api.post("/students/bulk", payload);
      const pmsg = res.parents_created ? L(` (${res.parents_created} parents invited)`, ` (${res.parents_created} parents invités)`) : "";
      toast.success(L(`${res.created} students created`, `${res.created} élèves créés`) + pmsg);
      close(); onDone && onDone();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setCreating(false); }
  };

  const completeCount = rows.filter(isReady).length;

  return (
    <Modal open={open} onClose={close} xwide title={L("Scan roster & bulk-add students", "Scanner une liste & ajouter en masse")}
      footer={rows.length > 0 ? (
        <div className="flex items-center gap-3">
          {dupCount > 0 && <span className="text-xs font-semibold text-red-600" data-testid="roster-dup-count">{L(`${dupCount} duplicate(s) skipped`, `${dupCount} doublon(s) ignoré(s)`)}</span>}
          <span className="text-xs text-muted-foreground">{L(`${completeCount} of ${rows.length} rows ready`, `${completeCount} sur ${rows.length} lignes prêtes`)}</span>
          <Button onClick={create} disabled={creating || completeCount === 0} data-testid="roster-create-btn">{creating ? L("Creating…", "Création…") : L(`Create ${completeCount} students`, `Créer ${completeCount} élèves`)}</Button>
        </div>
      ) : null}>
      <div className="space-y-3">
        <p className="text-sm text-muted-foreground">{L("Upload a PDF or photo of a class list. Each detected student becomes an editable row — set the grade, fix any details, then create them all at once.", "Téléversez un PDF ou une photo d'une liste de classe. Chaque élève détecté devient une ligne modifiable — choisissez le niveau, corrigez les détails, puis créez-les tous d'un coup.")}</p>
        <div className="flex items-center gap-3 bg-secondary/50 border border-border rounded-lg p-3">
          <ScanLine className="h-5 w-5 text-primary shrink-0" />
          <input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => scan(e.target.files?.[0])} data-testid="roster-file-input" className="text-sm" disabled={scanning} />
          {scanning && <span className="text-xs text-muted-foreground">{L("Reading roster…", "Lecture de la liste…")}</span>}
        </div>

        {rows.length > 0 && (
          <div className="max-h-[50vh] overflow-auto border border-border rounded-lg" data-testid="roster-review-table">
            <Table>
              <TableHeader><TableRow>
                <TableHead>{L("First", "Prénom")}</TableHead>
                <TableHead>{L("Middle", "2e prénom")}</TableHead>
                <TableHead>{L("Last", "Nom")}</TableHead>
                <TableHead>{L("DOB", "Naissance")}</TableHead>
                <TableHead>{L("Sex", "Sexe")}</TableHead>
                <TableHead>{L("Grade", "Niveau")}</TableHead>
                <TableHead>{L("Parent name", "Nom du parent")}</TableHead>
                <TableHead>{L("Parent email", "Email du parent")}</TableHead>
                <TableHead />
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r._id} data-testid={`roster-row-${r._id}`} className={isDup(r) && !r.include ? "bg-red-50" : ""}>
                    <TableCell><Input value={r.first_name} onChange={(e) => upd(r._id, "first_name", e.target.value)} className="h-8 min-w-[8rem]" data-testid={`roster-first-${r._id}`} /></TableCell>
                    <TableCell><Input value={r.middle_name} onChange={(e) => upd(r._id, "middle_name", e.target.value)} className="h-8 min-w-[7rem]" data-testid={`roster-middle-${r._id}`} /></TableCell>
                    <TableCell><Input value={r.last_name} onChange={(e) => upd(r._id, "last_name", e.target.value)} className="h-8 min-w-[8rem]" data-testid={`roster-last-${r._id}`} /></TableCell>
                    <TableCell><Input type="date" value={r.dob} onChange={(e) => upd(r._id, "dob", e.target.value)} className="h-8 w-36" data-testid={`roster-dob-${r._id}`} /></TableCell>
                    <TableCell>
                      <Select value={r.gender || undefined} onValueChange={(v) => upd(r._id, "gender", v)}>
                        <SelectTrigger className="h-8 w-20" data-testid={`roster-gender-${r._id}`}><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent><SelectItem value="M">M</SelectItem><SelectItem value="F">F</SelectItem></SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Select value={r.grade_level || undefined} onValueChange={(v) => upd(r._id, "grade_level", v)}>
                        <SelectTrigger className="h-8 w-44" data-testid={`roster-grade-${r._id}`}><SelectValue placeholder={L("Select", "Choisir")} /></SelectTrigger>
                        <SelectContent>{grades.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}</SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell><Input value={r.parent_name} onChange={(e) => upd(r._id, "parent_name", e.target.value)} className="h-8 min-w-[8rem]" data-testid={`roster-parent-name-${r._id}`} /></TableCell>
                    <TableCell><Input value={r.parent_email} onChange={(e) => upd(r._id, "parent_email", e.target.value)} className="h-8 min-w-[10rem]" placeholder="parent@…" data-testid={`roster-parent-email-${r._id}`} /></TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 whitespace-nowrap">
                        {isDup(r) && <span className="text-[10px] font-semibold text-red-600" data-testid={`roster-dup-${r._id}`}>{L("Exists", "Existe déjà")}</span>}
                        {isDup(r) && <label className="flex items-center gap-1 text-[10px] text-muted-foreground"><input type="checkbox" checked={!!r.include} onChange={(e) => upd(r._id, "include", e.target.checked)} data-testid={`roster-include-${r._id}`} />{L("import anyway", "importer quand même")}</label>}
                        <Button size="icon" variant="ghost" onClick={() => removeRow(r._id)} data-testid={`roster-remove-${r._id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}              </TableBody>
            </Table>
            <div className="p-2 border-t border-border">
              <Button size="sm" variant="outline" onClick={addRow} data-testid="roster-add-row-btn"><Plus className="h-4 w-4 mr-1" />{L("Add row", "Ajouter une ligne")}</Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
